import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../api/baseurl";
import heroDoctor from "../images/login_hero_doctor_transparent.png";
import "./employee-dashboard.css";

function EmployeeDashboardView({ user }) {
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState(null);
  const [upcomingAppt, setUpcomingAppt] = useState(null);
  const [upcomingCount, setUpcomingCount] = useState(0);
  const [familyCount, setFamilyCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const storedData = JSON.parse(localStorage.getItem("data") || "{}");
  const userid = user?.userid || storedData?.userid;

  useEffect(() => {
    const fetchEmployeeInfo = async () => {
      if (!userid) return;
      try {
        const token = storedData?.token;
        const res = await axios.get(`/api/employeedetails/userid=${userid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserDetails(res.data);
      } catch (err) {
        console.error("Error fetching employee details:", err);
      }
    };

    const fetchAppointments = async () => {
      if (!storedData?.token) return;
      try {
        const token = storedData?.token;
        const res = await axios.get(`/api/employeeappointment/getAllAppointments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const appts = res.data?.content || res.data || [];
        if (Array.isArray(appts) && appts.length > 0) {
          const activeAppts = appts.filter(
            (a) =>
              a.appStatus?.appointmentStatus !== "APPOINTMENT CANCELLED" &&
              a.appStatus?.appointmentStatus !== "APPOINTMENT COMPLETED"
          );
          setUpcomingCount(activeAppts.length);
          setUpcomingAppt(activeAppts[0] || appts[0]);
        }
      } catch (err) {
        console.error("Error fetching appointments:", err);
      }
    };

    const fetchFamilyDetails = async () => {
      if (!userid) return;
      try {
        const token = storedData?.token;
        const res = await axios.get(`/api/familydetails/userid=${userid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFamilyCount(res.data?.length || 0);
      } catch (err) {
        console.error("Error fetching family details:", err);
      }
    };

    fetchEmployeeInfo();
    fetchAppointments();
    fetchFamilyDetails();
  }, [userid]);

  // Dynamic time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  const rawName = userDetails?.name || storedData?.username || "Employee";
  const firstName = rawName.includes("@")
    ? rawName.split("@")[0].split(".")[0]
    : rawName.split(" ")[0];
  const capitalizedName = firstName.charAt(0).toUpperCase() + firstName.slice(1);

  const getApptDate = () => {
    if (upcomingAppt?.date) {
      const d = new Date(upcomingAppt.date);
      if (!isNaN(d.getTime())) {
        return {
          day: d.getDate(),
          month: d.toLocaleString("default", { month: "short" }),
          year: d.getFullYear(),
        };
      }
    }
    return { day: "24", month: "May", year: "2026" };
  };

  const apptDateInfo = getApptDate();

  return (
    <div className="col-12 mx-xl-auto ps-xl-10 mt-10 pe-xl-4">
      <div className="hm-web-dash-wrapper">
        
        {/* ── WEB HERO BANNER (COMPACT SLEEK DESKTOP BANNER) ── */}
        <div className="hm-web-hero-banner">
          <div className="hm-web-hero-glow" />
          <div className="row align-items-center">
            <div className="col-lg-7">
              <h2 className="hm-web-greeting">
                {getGreeting()}, {capitalizedName} <span role="img" aria-label="wave">👋</span>
              </h2>
              <p className="hm-web-sub">
                Manage your health checkups, doctors, and diagnostic reports in one place.
              </p>
            </div>
            <div className="col-lg-5 text-lg-end mt-2 mt-lg-0">
              <div className="hm-web-quick-stats">
                <div className="hm-web-stat-pill">
                  <i className="material-icons">event_available</i>
                  <div>
                    <strong>{upcomingCount}</strong> <span>Upcoming</span>
                  </div>
                </div>
                <div className="hm-web-stat-pill">
                  <i className="material-icons">people</i>
                  <div>
                    <strong>{familyCount}</strong> <span>Family Members</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── MAIN CONTENT DESKTOP GRID ── */}
        <div className="row mt-4 g-4">
          
          {/* ── LEFT MAIN COLUMN (8 COLS) ── */}
          <div className="col-lg-8">
            
            {/* SEARCH BAR */}
            <div className="hm-web-search-card mb-4">
              <i className="material-icons">search</i>
              <input
                type="text"
                placeholder="Search lab tests, packages, doctors, or clinics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="btn btn-primary btn-sm rounded-pill px-3 ms-2" type="button">
                Search
              </button>
            </div>

            {/* QUICK ACTIONS 4-GRID */}
            <h4 className="hm-web-section-header">Quick Healthcare Services</h4>
            <div className="row g-3 mb-4">
              <div className="col-md-6 col-6">
                <div
                  className="hm-web-action-card hm-web-card-mint"
                  onClick={() => navigate("/users/employee-appointments?tab=lab-appointments")}
                >
                  <div className="hm-web-action-icon hm-web-icon-mint">
                    <i className="material-icons">biotech</i>
                  </div>
                  <div>
                    <h5>Book a Test</h5>
                    <p>Schedule lab checkup & package</p>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-6">
                <div
                  className="hm-web-action-card hm-web-card-blue"
                  onClick={() => navigate("/users/employee-appointments?tab=doctor-appointments")}
                >
                  <div className="hm-web-action-icon hm-web-icon-blue">
                    <i className="material-icons">medical_services</i>
                  </div>
                  <div>
                    <h5>Consult a Doctor</h5>
                    <p>Book online or clinic consultation</p>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-6">
                <div
                  className="hm-web-action-card hm-web-card-yellow"
                  onClick={() => navigate("/users/reports")}
                >
                  <div className="hm-web-action-icon hm-web-icon-yellow">
                    <i className="material-icons">insert_drive_file</i>
                  </div>
                  <div>
                    <h5>My Reports</h5>
                    <p>View & download test results</p>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-6">
                <div
                  className="hm-web-action-card hm-web-card-peach"
                  onClick={() => navigate("/users/family-details")}
                >
                  <div className="hm-web-action-icon hm-web-icon-peach">
                    <i className="material-icons">groups</i>
                  </div>
                  <div>
                    <h5>Family Details</h5>
                    <p>Manage family profiles</p>
                  </div>
                </div>
              </div>
            </div>

            {/* UPCOMING APPOINTMENT SECTION */}
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h4 className="hm-web-section-header m-0">Upcoming Appointment</h4>
              <button
                className="btn btn-link p-0 hm-web-link"
                onClick={() => navigate("/users/employee-appointments")}
                type="button"
              >
                View All Appointments &rarr;
              </button>
            </div>

            {upcomingAppt ? (
              <div className="hm-web-appt-card mb-4" onClick={() => navigate("/users/employee-appointments")}>
                <div className="hm-web-appt-date">
                  <span className="hm-web-date-num">{apptDateInfo.day}</span>
                  <span className="hm-web-date-month">{apptDateInfo.month}</span>
                </div>
                <div className="hm-web-appt-details flex-grow-1">
                  <div className="d-flex align-items-center gap-2">
                    <h5 className="m-0">{upcomingAppt?.doc?.name || upcomingAppt?.doctorName || upcomingAppt?.labs?.labname || "N/A"}</h5>
                    <span className="badge bg-success-soft text-success">
                      {upcomingAppt?.appStatus?.appointmentStatus || "Scheduled"}
                    </span>
                  </div>
                  <p className="text-muted small m-0 mt-1">
                    {upcomingAppt?.pack?.packagename || ""}
                  </p>
                  <div className="hm-web-appt-meta mt-2">
                    {upcomingAppt?.date && (
                      <span><i className="material-icons">calendar_today</i> {upcomingAppt.date}</span>
                    )}
                    {upcomingAppt?.time && (
                      <span><i className="material-icons">schedule</i> {upcomingAppt.time}</span>
                    )}
                    {(upcomingAppt?.company?.companyname || upcomingAppt?.labs?.labname) && (
                      <span><i className="material-icons">location_on</i> {upcomingAppt?.company?.companyname || upcomingAppt?.labs?.labname}</span>
                    )}
                  </div>
                </div>
                <button className="btn btn-outline-primary btn-sm rounded-pill ms-3" type="button">
                  Details
                </button>
              </div>
            ) : (
              <div className="hm-web-no-appt-card mb-4">
                <i className="material-icons hm-web-no-appt-icon">event_busy</i>
                <p className="m-0 fw-semibold">No upcoming appointments</p>
                <p className="text-muted small m-0">Book a lab test or consult a doctor to get started.</p>
                <button
                  className="btn btn-primary btn-sm rounded-pill mt-3 px-4"
                  onClick={() => navigate("/users/employee-appointments")}
                  type="button"
                >
                  Book Now
                </button>
              </div>
            )}
          </div>

          {/* ── RIGHT SIDEBAR COLUMN (4 COLS) ── */}
          <div className="col-lg-4">
            
            {/* USER PROFILE SUMMARY WIDGET */}
            <div className="card card-raised mb-4">
              <div className="card-body">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="hm-web-avatar-circle">
                    {capitalizedName.charAt(0)}
                  </div>
                  <div>
                    <h6 className="m-0 fw-bold">{capitalizedName}</h6>
                    <span className="text-muted small">{storedData?.username || "Employee"}</span>
                  </div>
                </div>
                <hr className="my-3" />
                <div className="d-flex justify-content-between align-items-center small mb-2">
                  <span className="text-muted">Organization Code:</span>
                  <span className="fw-bold">{storedData?.orgcode || "CYN001"}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center small mb-3">
                  <span className="text-muted">Annual Checkup Status:</span>
                  <span className="badge bg-success">Eligible</span>
                </div>
                <button
                  className="btn btn-outline-secondary btn-sm w-100 rounded-pill"
                  onClick={() => navigate("/users/view-employee-profile")}
                  type="button"
                >
                  View Full Profile
                </button>
              </div>
            </div>

            {/* QUICK SHORTCUTS SIDEBAR WIDGET */}
            <div className="card card-raised mb-4">
              <div className="card-header bg-transparent fw-bold py-3">
                <i className="material-icons align-middle me-2 text-primary">bolt</i>
                Quick Shortcuts
              </div>
              <div className="list-group list-group-flush">
                <button
                  type="button"
                  className="list-group-item list-group-item-action d-flex align-items-center justify-content-between py-3"
                  onClick={() => navigate("/users/employee-appointments?tab=lab-appointments")}
                >
                  <span><i className="material-icons text-success me-2 align-middle">science</i> Lab Appointments</span>
                  <i className="material-icons text-muted small">chevron_right</i>
                </button>

                <button
                  type="button"
                  className="list-group-item list-group-item-action d-flex align-items-center justify-content-between py-3"
                  onClick={() => navigate("/users/employee-appointments?tab=doctor-appointments")}
                >
                  <span><i className="material-icons text-primary me-2 align-middle">medical_services</i> Doctor Consultations</span>
                  <i className="material-icons text-muted small">chevron_right</i>
                </button>

                <button
                  type="button"
                  className="list-group-item list-group-item-action d-flex align-items-center justify-content-between py-3"
                  onClick={() => navigate("/users/family-details")}
                >
                  <span><i className="material-icons text-warning me-2 align-middle">groups</i> Family Members</span>
                  <i className="material-icons text-muted small">chevron_right</i>
                </button>

                <button
                  type="button"
                  className="list-group-item list-group-item-action d-flex align-items-center justify-content-between py-3"
                  onClick={() => navigate("/users/reports")}
                >
                  <span><i className="material-icons text-danger me-2 align-middle">description</i> Diagnostic Reports</span>
                  <i className="material-icons text-muted small">chevron_right</i>
                </button>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}

export default EmployeeDashboardView;
